import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { FaSearch, FaDumbbell, FaClock, FaFilter, FaTimes, FaPlus } from 'react-icons/fa';
import SEO from '../SEO';
import { PAGE_SEO, buildBreadcrumbs } from '../../utils/seoConfig';
import './ExercisesListPage.css';

const ExercisesListPage = () => {
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [selectedMeasurement, setSelectedMeasurement] = useState('');
  const [showMineOnly, setShowMineOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalExercises, setTotalExercises] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    difficulties: [],
    equipment: [],
    measurementTypes: [],
    targetMuscles: [],
    tags: []
  });
  const observerTarget = useRef(null);
  const listFetchAbortRef = useRef(null);
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    if (!isLoggedIn) setShowMineOnly(false);
  }, [isLoggedIn]);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/fitness/exercises-filters`);
        if (response.ok) {
          const data = await response.json();
          setFilterOptions(data);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch exercises with pagination (signal avoids stale load-more after search/filter changes)
  const fetchExercises = useCallback(async (pageNum = 1, isLoadMore = false, signal) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params = new URLSearchParams();
      params.append('page', pageNum);
      params.append('limit', 50);
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      if (selectedMuscle) params.append('targetMuscle', selectedMuscle);
      if (selectedEquipment) params.append('equipment', selectedEquipment);
      if (selectedMeasurement) params.append('measurementType', selectedMeasurement);
      if (showMineOnly) params.append('mine', '1');

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/fitness/exercises-paginated?${params}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal
        }
      );
      if (signal?.aborted) return;
      if (!response.ok) throw new Error('Failed to fetch exercises');
      const data = await response.json();
      if (signal?.aborted) return;

      if (isLoadMore) {
        setExercises(prev => [...prev, ...data.exercises]);
      } else {
        setExercises(data.exercises);
      }

      setTotalExercises(data.pagination.total);
      setHasMore(data.pagination.hasMore);
      setPage(pageNum);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
    } finally {
      if (signal?.aborted) {
        setLoadingMore(false);
        return;
      }
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearchTerm, selectedCategory, selectedDifficulty, selectedMuscle, selectedEquipment, selectedMeasurement, showMineOnly]);

  // Reset list when search/filters change (abort in-flight requests so load-more cannot corrupt results)
  useEffect(() => {
    listFetchAbortRef.current?.abort();
    const ac = new AbortController();
    listFetchAbortRef.current = ac;
    setPage(1);
    fetchExercises(1, false, ac.signal);
    return () => ac.abort();
  }, [debouncedSearchTerm, selectedCategory, selectedDifficulty, selectedMuscle, selectedEquipment, selectedMeasurement, showMineOnly, fetchExercises]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        fetchExercises(page + 1, true, listFetchAbortRef.current?.signal);
      }
    }, { threshold: 0.1 });

    const el = observerTarget.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [page, hasMore, loadingMore, loading, fetchExercises]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedDifficulty('');
    setSelectedMuscle('');
    setSelectedEquipment('');
    setSelectedMeasurement('');
    setShowMineOnly(false);
    setPage(1);
  };

  const hasActiveFilters =
    debouncedSearchTerm ||
    selectedCategory ||
    selectedDifficulty ||
    selectedMuscle ||
    selectedEquipment ||
    selectedMeasurement ||
    showMineOnly;

  if (loading && !exercises.length && !searchTerm && !selectedCategory && !selectedDifficulty && !selectedMuscle && !selectedEquipment && !selectedMeasurement && !showMineOnly) {
    return (
      <div className="exercises-list-page">
        <div className="exercises-loading">
          <div className="loading-spinner" />
          <p>Loading exercises...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="exercises-list-page">
        <div className="exercises-error">
          <p>Error: {error}</p>
          <button onClick={() => navigate('/fitness')}>Back to Fitness</button>
        </div>
      </div>
    );
  }

  const seo = PAGE_SEO['/fitness/exercises'];

  return (
    <div className="exercises-list-page">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/fitness/exercises"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seo.title,
            description: seo.description,
            url: 'https://freshfitlab.io/fitness/exercises',
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Fitness', url: '/fitness' },
            { name: 'Exercises', url: '/fitness/exercises' },
          ]),
        ]}
      />
      <div className="exercises-header">
        <div className="header-content">
          <h1>Exercise Library</h1>
          <p>Browse all available exercises</p>
        </div>
        {isLoggedIn && (
          <button className="create-exercise-btn" onClick={() => navigate('/fitness/exercise/create')}>
            <FaPlus /> Create Exercise
          </button>
        )}
      </div>

      <div className="exercises-container">
        {/* Search and Filters */}
        <div className="search-filter-section">
          <div className="exercises-search-box">
            <FaSearch className="exercises-search-icon" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="exercises-clear-search" onClick={() => setSearchTerm('')}>
                <FaTimes />
              </button>
            )}
          </div>

          <button 
            className={`exercises-filter-toggle ${showFilters ? 'exercises-filter-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            <span>Filters</span>
            {hasActiveFilters && <span className="exercises-filter-badge" />}
          </button>
        </div>

        {showFilters && (
          <div className="filters-panel">
            {isLoggedIn && (
              <div className="filter-group filter-group-checkbox">
                <label className="filter-checkbox-label">
                  <input
                    type="checkbox"
                    checked={showMineOnly}
                    onChange={e => setShowMineOnly(e.target.checked)}
                  />
                  <span>Created by me</span>
                </label>
                <p className="filter-hint">Only exercises you created or imported</p>
              </div>
            )}
            {/* Category filter hidden - most exercises are 'strength' or 'core' */}
            {filterOptions.categories && filterOptions.categories.length > 3 && (
              <div className="filter-group">
                <label>Category</label>
                <select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {filterOptions.categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-group">
              <label>Difficulty</label>
              <select 
                value={selectedDifficulty} 
                onChange={(e) => setSelectedDifficulty(e.target.value)}
              >
                <option value="">All Levels</option>
                {filterOptions.difficulties.map(diff => (
                  <option key={diff} value={diff}>{diff}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Muscle Group</label>
              <select 
                value={selectedMuscle} 
                onChange={(e) => setSelectedMuscle(e.target.value)}
              >
                <option value="">All Muscles</option>
                {filterOptions.targetMuscles.map(muscle => (
                  <option key={muscle} value={muscle}>{muscle}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Equipment</label>
              <select 
                value={selectedEquipment} 
                onChange={(e) => setSelectedEquipment(e.target.value)}
              >
                <option value="">All Equipment</option>
                {filterOptions.equipment.map(eq => (
                  <option key={eq.name} value={eq.name}>{eq.name}</option>
                ))}
              </select>
            </div>

            {/* Measurement Type filter hidden - not needed for user experience */}

            {hasActiveFilters && (
              <button className="clear-filters" onClick={clearFilters}>
                Clear All Filters
              </button>
            )}
          </div>
        )}

        {/* Results count */}
        <div className="results-count">
          Showing {exercises.length} of {totalExercises} exercise{totalExercises !== 1 ? 's' : ''}
        </div>

        {/* Exercises Grid */}
        <div className="exercises-grid">
          {exercises.map(exercise => (
            <div 
              key={exercise.exerciseId}
              className="exercise-card"
              onClick={() => navigate(`/fitness/exercise/${exercise.exerciseId}`)}
            >
              <div className="exercise-image">
                {exercise.image ? (
                  <img src={exercise.image} alt={exercise.name} />
                ) : (
                  <div className="exercise-placeholder">
                    <FaDumbbell />
                  </div>
                )}
                {exercise.category && (
                  <span className="exercise-category">{exercise.category}</span>
                )}
                {exercise.difficulty && (
                  <span className={`exercise-difficulty difficulty-${exercise.difficulty}`}>
                    {exercise.difficulty}
                  </span>
                )}
                {/* Title overlay on image */}
                <div className="exercise-title-overlay">
                  <h3>{exercise.name}</h3>
                  {exercise.targetMuscles && exercise.targetMuscles.length > 0 && (
                    <div className="overlay-muscles">
                      {exercise.targetMuscles.slice(0, 2).map((muscle, i) => (
                        <span key={i}>{muscle}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="exercise-content">
                <h3 className="exercise-name">{exercise.name}</h3>
                
                {exercise.description && (
                  <p className="exercise-description">
                    {exercise.description.length > 80 
                      ? exercise.description.substring(0, 80) + '...'
                      : exercise.description}
                  </p>
                )}

                <div className="exercise-meta">
                  {exercise.targetMuscles && exercise.targetMuscles.length > 0 && (
                    <div className="muscle-tags">
                      {exercise.targetMuscles.slice(0, 3).map((muscle, i) => (
                        <span key={i} className="muscle-tag primary">{muscle}</span>
                      ))}
                    </div>
                  )}

                  <div className="exercise-stats">
                    {exercise.measurementType === 'time' && exercise.defaultDuration && (
                      <span className="stat">
                        <FaClock />
                        {Math.floor(exercise.defaultDuration / 60)}:{(exercise.defaultDuration % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                    {exercise.measurementType === 'reps' && exercise.defaultReps && (
                      <span className="stat">
                        <FaDumbbell />
                        {exercise.defaultReps} reps
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load more observer target */}
        {hasMore && <div ref={observerTarget} className="observer-target" />}

        {/* Loading more indicator */}
        {loadingMore && (
          <div className="loading-more">
            <div className="loading-spinner small" />
            <p>Loading more exercises...</p>
          </div>
        )}

        {exercises.length === 0 && !loading && (
          <div className="no-results">
            <FaDumbbell />
            <h3>No exercises found</h3>
            <p>Try adjusting your search or filters</p>
            <button onClick={clearFilters}>Clear Filters</button>
          </div>
        )}

        {!hasMore && exercises.length > 0 && (
          <div className="end-of-results">
            <p>You've reached the end ({exercises.length} exercises loaded)</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExercisesListPage;

